import { useCallback, useEffect, useState } from "react";
import { createContainer } from "unstated-next";
import { AxiosError } from "axios";
import { initializeApp } from "firebase/app";
import {
    GoogleAuthProvider,
    OAuthProvider,
    getAuth,
    signInWithPopup,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    sendEmailVerification,
    sendPasswordResetEmail,
    signOut,
    deleteUser,
    User as FirebaseUser,
    UserCredential
} from "firebase/auth";

import { ThemeContainer } from "../theme/theme.container";
import { TivaDataWebSocketContainer } from "../tiva-data/tiva-data-web-socket.container";
import { useFirebaseToken } from "./use-firebase-token.util";
import { firebaseConfig } from "../../utilities/constants/constants";
import { useTimer } from "../../utilities/timer/use-timer.util";
import { isAdminPanel } from "../../utilities/routes/routes.util";
import { isAxios } from "../../utilities/errors/errors.util";
import { stringGetter } from "../../utilities/localstorage-dealer/localstorage-getters.util";
import {
    setDataToLocalStorage,
    removeDataToLocalStorage
} from "../../utilities/localstorage-dealer/localstorage-setter.util";
import { AuthEvents } from "../../enums/auth-events.enum";
import { CompanyType } from "../../enums/company-type.enum";
import { FirebaseErrorCode } from "../../enums/firebase-error-code.enum";
import { UserRole } from "../../enums/user-role.enum";
import { User } from "../../interfaces/comps/user.interface";
import { KnowledgeBase } from "../../interfaces/admin/knowledge-base.interface";
import { Config } from "../../interfaces/comps/config.interface";
import { ACCESS_TOKEN_KEY } from "../../../api/base";
import { fetchVersion, fetchConfig } from "../../../api/admin/config/endpoints-referrers";
import { fetchUser, updateUser } from "../../../api/users/endpoints-referrers";
import { fetchKnowledgeBases } from "../../../api/admin/knowledge-base/endpoints-referrers";
import { registerFirebase, fetchTivaToken } from "../../../api/auth/endpoints-referrers";

function useFirebaseAuth() {
    const googleProvider = new GoogleAuthProvider();
    const microsoftProvider = new OAuthProvider("microsoft.com");
    const app = initializeApp(firebaseConfig);
    const auth = getAuth(app);
    const themeContainer = ThemeContainer.useContainer();
    const { setTivaApp } = TivaDataWebSocketContainer.useContainer();
    const [config, setConfig] = useState<Config>();
    const [version, setVersion] = useState<string>();
    const [isTrial, setIsTrial] = useState<boolean>(true);
    const [isAdmin, setIsAdmin] = useState<boolean>();
    const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>();
    const [user, setUser] = useState<User | null>(null);
    const [email, setEmail] = useState<string>();
    const [emailVerified, setEmailVerified] = useState<boolean>();
    const [providerLoading, setProviderLoading] = useState<boolean>(false);
    const [sendVerificationWaitingTime, setSendVerificationWaitingTime] = useState<number>();
    const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
    const {
        currentAccessToken,
        isAuthenticated,
        setAccessToken,
        setTokens,
        clearToken,
        updateCurrentAccessToken
    } = useFirebaseToken(auth, useCallback(clearUser, []));
    const { timerStarted, timerTimeLeft, startTimer, stopTimer } = useTimer();

    useEffect(() => {
        setIsAdmin(
            !user ? undefined : user.role === UserRole.admin || user.role === UserRole.manager
        );
        setIsSuperAdmin(user?.role === UserRole.admin);
    }, [user]);

    useEffect(() => {
        if (currentAccessToken && user && !isAdminPanel) {
            fetchTivaToken().then(result => {
                setTivaApp(result?.data);
            });
        } else {
            setTivaApp(undefined);
        }
    }, [currentAccessToken, user, setTivaApp]);

    useEffect(() => {
        fetchVersion().then(response => {
            setVersion(response.data);
        });
    }, []);

    useEffect(() => {
        if (user) {
            refreshConfig();
        } else {
            setConfig(undefined);
        }
    }, [user]);

    const refreshConfig = async () => {
        const response = await fetchConfig();
        setConfig(response.data);
    };

    const getUser = useCallback(async () => {
        const {
            data: { ...user }
        } = await fetchUser();

        if (isAdminPanel && user.role !== UserRole.manager && user.role !== UserRole.admin) {
            throw { code: FirebaseErrorCode.accessPermissions };
        }

        setUser(user);
        await getKnowledgeBases(user.companyId);
    }, []);

    const getKnowledgeBases = async (companyId: number) => {
        const {
            data: { items }
        } = await fetchKnowledgeBases(companyId);
        setKnowledgeBases(items);
    };

    const logout = useCallback(() => {
        signOut(auth).finally(async () => {
            await clearToken(true);
            stopTimer();
            setDataToLocalStorage(AuthEvents.LOGOUT, new Date().toISOString());
            removeDataToLocalStorage(AuthEvents.VERIFICATION_SENDING_TIME);
        });
    }, [auth, clearToken, stopTimer]);

    const loadUserData = useCallback(
        (user: FirebaseUser | null) => {
            const isVerified = !!user?.emailVerified || !!user?.providerId;
            setEmail(user?.email ?? undefined);
            setEmailVerified(isVerified);
            if (isVerified) {
                getUser().catch(() => {
                    logout();
                });
            } else {
                const now = new Date().getTime();
                const sendingTime = stringGetter(AuthEvents.VERIFICATION_SENDING_TIME);
                if (sendingTime && now - new Date(sendingTime).getTime() < 60000) {
                    startTimer(60 - Math.trunc((now - new Date(sendingTime).getTime()) / 1000));
                }
            }
        },
        [getUser, startTimer, logout]
    );

    useEffect(() => {
        setIsTrial(user?.companyType === CompanyType.trial);
    }, [user]);

    useEffect(() => {
        if (isAuthenticated()) {
            auth.authStateReady().then(() => {
                loadUserData(auth.currentUser);
            });
        }
    }, [auth, isAuthenticated, loadUserData]);

    useEffect(() => {
        const handleStorageEventListener = async (event: WindowEventMap["storage"]) => {
            if (event.key === AuthEvents.LOGOUT) {
                await clearToken();
                stopTimer();
            } else if (
                (event.key === AuthEvents.LOGIN ||
                    event.key === AuthEvents.VERIFICATION_SENDING_TIME ||
                    event.key === AuthEvents.EMAIL_CONFIRMED_TIME) &&
                isAuthenticated()
            ) {
                location.reload();
            } else if (event.key === ACCESS_TOKEN_KEY && isAuthenticated()) {
                updateCurrentAccessToken();
            }
        };

        window.addEventListener("storage", handleStorageEventListener);

        return () => {
            window.removeEventListener("storage", handleStorageEventListener);
        };
    }, [loadUserData, isAuthenticated, clearToken, stopTimer, updateCurrentAccessToken]);

    useEffect(() => {
        setSendVerificationWaitingTime(timerStarted ? timerTimeLeft : undefined);
    }, [timerStarted, timerTimeLeft]);

    const signIn = async (email: string, password: string) => {
        try {
            const response = await signInWithEmailAndPassword(auth, email, password);
            const firebaseUser = response.user;
            const token = await firebaseUser.getIdToken();
            setAccessToken(token);
            setEmailVerified(firebaseUser.emailVerified);
            setEmail(firebaseUser.email ?? undefined);

            if (!firebaseUser.emailVerified) {
                await sendVerification();
            } else {
                await getUser();
                setDataToLocalStorage(AuthEvents.LOGIN, new Date().toISOString());
            }

            setTokens({ accessToken: token, refreshToken: firebaseUser.refreshToken });

            return Promise.resolve(response);
        } catch (error: any) {
            // if (error?.code === FirebaseErrorCode.manyRequests) {
            //     return Promise.resolve({});
            // } else {
            //     logout();
            //     return Promise.reject(error);
            // }
            logout();
            return Promise.reject(error);
        }
    };

    const signUp = async (
        email: string,
        password: string,
        invite: string | null,
        name?: string,
        companyName?: string,
        position?: string
    ) => {
        try {
            const response = await createUserWithEmailAndPassword(auth, email, password);
            const firebaseUser = response.user;
            const token = await firebaseUser.getIdToken();
            setAccessToken(token);
            setEmailVerified(firebaseUser.emailVerified);
            setEmail(firebaseUser.email ?? undefined);

            await registerFirebaseUser(
                firebaseUser,
                email,
                invite,
                name,
                companyName,
                position,
                themeContainer.isTrial && isAdminPanel
            );
            await sendVerification();
            setTokens({ accessToken: token, refreshToken: firebaseUser.refreshToken });

            return Promise.resolve(response);
        } catch (error) {
            logout();
            return Promise.reject(error);
        }
    };

    const signInWithGoogle = async (invite: string | null) => {
        try {
            const response = await signInWithPopup(auth, googleProvider);
            return await signInWithProvider(response, invite);
        } catch (error) {
            logout();
            return Promise.reject(error);
        }
    };

    const signInWithMicrosoft = async (invite: string | null) => {
        try {
            const response = await signInWithPopup(auth, microsoftProvider);
            return await signInWithProvider(response, invite);
        } catch (error) {
            logout();
            return Promise.reject(error);
        }
    };

    const signInWithProvider = async (response: UserCredential, invite: string | null) => {
        try {
            setProviderLoading(true);

            const firebaseUser = response.user;
            const token = await firebaseUser.getIdToken();
            setAccessToken(token);

            if (!firebaseUser.email) {
                throw { code: FirebaseErrorCode.invalidEmail };
            }

            setEmail(firebaseUser.email);
            setEmailVerified(firebaseUser.emailVerified || !!firebaseUser.providerId);
            await getOrRegisterUser(firebaseUser, firebaseUser.email, invite);

            setTokens({ accessToken: token, refreshToken: firebaseUser.refreshToken });
            setDataToLocalStorage(AuthEvents.LOGIN, new Date().toISOString());

            return Promise.resolve(response);
        } catch (error) {
            return Promise.reject(error);
        } finally {
            setProviderLoading(false);
        }
    };

    const getOrRegisterUser = async (user: FirebaseUser, email: string, invite: string | null) => {
        if (!!invite || (isAdminPanel && themeContainer.isTrial)) {
            await registerFirebaseUser(
                user,
                email,
                invite,
                undefined,
                undefined,
                undefined,
                themeContainer.isTrial && isAdminPanel
            );
            await getUser();
        } else {
            try {
                await getUser();
            } catch (error) {
                if (error instanceof AxiosError && error.response?.status === 422) {
                    await deleteUser(user);
                    throw { code: FirebaseErrorCode.invalidInvite };
                }
                throw error;
            }
        }
    };

    const registerFirebaseUser = async (
        user: FirebaseUser,
        email: string,
        invite: string | null,
        name?: string,
        companyName?: string,
        position?: string,
        forceCreateKB = false
    ) => {
        const signupData = {
            email,
            from: invite,
            firebaseId: user.uid,
            firstName: name,
            companyName: companyName ?? crypto.randomUUID(),
            position,
            forceCreateKB
        };
        try {
            await registerFirebase(signupData);
        } catch (error) {
            if (error instanceof AxiosError && error.response?.status === 409) {
                await updateUser(user.uid);
            } else {
                await clearNonexistentUser(user);
                throw isAxios(error, 403, "Invalid invite")
                    ? { code: FirebaseErrorCode.invalidInvite }
                    : isAxios(error, 403, "Invalid email format")
                      ? { code: FirebaseErrorCode.invalidEmail }
                      : isAxios(error, 403, "Database change denied")
                        ? { code: FirebaseErrorCode.forbiddenInvite }
                        : error;
            }
        }
    };

    const clearNonexistentUser = async (user: FirebaseUser) => {
        try {
            await fetchUser();
        } catch (userError) {
            if (userError instanceof AxiosError && userError.response?.status === 422) {
                await deleteUser(user);
            }
        }
    };

    const sendVerification = async () => {
        try {
            if (!auth.currentUser) return Promise.resolve(false);

            setDataToLocalStorage(AuthEvents.VERIFICATION_SENDING_TIME, new Date().toISOString());
            startTimer(60);
            await sendEmailVerification(auth.currentUser, {
                url: `${window.location.origin}/registration-success`
            });

            return Promise.resolve(true);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    const sendPasswordReset = async (email: string) => {
        try {
            await sendPasswordResetEmail(auth, email, {
                url: `${window.location.origin}/login`
            });

            return Promise.resolve(true);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    function clearUser() {
        setUser(null);
        setEmail(undefined);
        setEmailVerified(undefined);
    }

    return {
        isAuthenticated,
        currentAccessToken,
        user,
        isSuperAdmin,
        isAdmin,
        isTrial,
        email,
        emailVerified,
        sendVerificationWaitingTime,
        knowledgeBases,
        config,
        version,
        providerLoading,
        refreshConfig,
        setUser,
        signIn,
        signUp,
        signInWithGoogle,
        signInWithMicrosoft,
        sendVerification,
        sendPasswordReset,
        logout
    };
}

export const FirebaseAuthContainer = createContainer(useFirebaseAuth);
